<template>
  <SignInvestmentLoader
    v-if="UIStore.is_loading && !isShowNormalLoader"
    ref="player"
    :key="UIStore.is_dark_mode"
    :animation-data="animationData"
  />
  <LoadingSpinner :show="UIStore.is_loading && isShowNormalLoader" size="large" smallText />
  <ReviewEditModal
    v-if="selectedComponentDetails"
    :open="isReviewEditModalOpen"
    :componentDetails="selectedComponentDetails"
    @close-modal="handleCancel"
    @save-changes="handleSaveChanges"
  />
  <SuitabilityRevisionModal
    :open="isRevisionModalOpen"
    :issues="suitabilityStructuredIssues"
    @close="handleRevisionModalClose"
    @save="handleRevisionModalSave"
  />
  <div class="mt-5 lg:mt-10">
    <DetailBanner
      v-if="
        dataStore.s1_offerings_in_quiet_period && purchaseFlowStore.selected_offering?.type === 'S1'
      "
      variant="secondary"
      class="mb-4"
    >
      <template #body>
        <AsteriskNotice text="Registered Offering Quiet Period" />
        <p class="font-inter font-medium text-strongest">
          Your payment will be processed upon submission. Your signature will be collected once the
          quiet period ends — we'll notify you when it's time to finalize.
        </p>
      </template>
    </DetailBanner>

    <!-- All suitability issues resolved banner -->
    <div
      v-if="suitabilityIssueState === 'fixed' && !purchaseFlowStore.suitability_review_error"
      class="mb-6 p-4 rounded-2xl border border-success bg-container-bg animate-slideup"
    >
      <p class="font-semibold text-success flex items-center gap-2 font-inter text-sm">
        <Icon name="Check" class="h-4 w-4" />
        All issues fixed
      </p>
      <p class="font-inter font-medium text-strongest mt-1">
        You are now ready to complete your investment.
      </p>
    </div>

    <div
      v-for="(step, index) in stepCardsToShow"
      :key="step.name"
      class="mb-6"
      :class="
        step.name === 'Suitability Questionnaire' && suitabilityIssueState === 'has_issues'
          ? 'border border-blue-800 rounded-xl'
          : ''
      "
    >
      <PaperCard
        :ref="
          (el) => {
            if (el) cardRefs[step.name] = el;
          }
        "
        is_collapsable
        :start_collapsed="true"
      >
        <template #header>
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <h2 class="font-semibold text-base lg:text-lg text-strongest shrink-0 font-inter">
              {{ index + 1 }}. {{ step.label }}
            </h2>
            <span
              v-if="
                step.name === 'Suitability Questionnaire' && suitabilityIssueState === 'has_issues'
              "
              class="text-info text-sm font-semibold font-inter flex items-center gap-1 shrink-0"
            >
              <Icon name="Asterisk" class="h-3.5 w-3.5 -skew-x-12" />
              <template v-if="suitabilityStructuredIssues.length > 0">
                {{ suitabilityStructuredIssues.length }}
                {{ suitabilityStructuredIssues.length === 1 ? "answer needs" : "answers need" }}
                updating
              </template>
              <template v-else>Financial info needs updating</template>
            </span>
          </div>
        </template>
        <template #ctas>
          <div
            v-if="
              step.name === 'Suitability Questionnaire' &&
              suitabilityIssueState === 'has_issues' &&
              suitabilityStructuredIssues.length > 0
            "
            class="hidden lg:block"
          >
            <Button @click="openRevisionModal" variant="primary-grey">
              <template #label>Update answers</template>
            </Button>
          </div>
          <Button
            @click="openEditModal(step)"
            variant="paper-primary"
            :disabled="step.name === 'Funding and Distribution' && cardOnHold"
          >
            <template #label>Edit</template>
          </Button>
        </template>
        <template #body>
          <component :is="step.component" is_review />
        </template>
      </PaperCard>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import { cloneDeep } from "lodash";
import { usePurchaseFlowStore, useUIStore, useDataStore } from "@/stores";
import {
  PaperCard,
  Button,
  Icon,
  SignInvestmentLoader,
  DetailBanner,
  AsteriskNotice,
  LoadingSpinner,
} from "@/components";
import { isRetirementAccountType } from "@/utilities";

import ReviewEditModal from "./PurchaseFlowUtilities/ReviewEditModal.vue";
import SuitabilityRevisionModal from "./PurchaseFlowUtilities/SuitabilityRevisionModal.vue";
import PF_PersonalInfo from "./PF_PersonalInfo.vue";
import PF_SuitabilityInfo from "./PF_SuitabilityInfo.vue";
import PF_JointInfo from "./PF_JointInfo.vue";
import PF_EntityInfo from "./PF_EntityInfo.vue";
import PF_FundingDistribution from "./PF_FundingDistribution.vue";
import { useStripeCheckout } from "@/composables/useStripeCheckout";

import preparingDocLight from "@/assets/animations/preparing-document-light.json";
import preparingDocDark from "@/assets/animations/preparing-document-dark.json";

const purchaseFlowStore = usePurchaseFlowStore();
const UIStore = useUIStore();
const dataStore = useDataStore();
const stripeCheckout = useStripeCheckout();

const cardRefs = ref({});

const componentMap = {
  "Personal Information": PF_PersonalInfo,
  "Suitability Questionnaire": PF_SuitabilityInfo,
  "Joint Information": PF_JointInfo,
  "Entity Information": PF_EntityInfo,
  "Funding and Distribution": PF_FundingDistribution,
};

const saveMap = {
  "Personal Information": () => purchaseFlowStore.saveOwnerProgress(),
  "Suitability Questionnaire": () => purchaseFlowStore.saveOwnerProgress(),
  "Joint Information": () => purchaseFlowStore.saveJointProgress(),
  "Entity Information": () => purchaseFlowStore.saveEntityProgress(),
  "Funding and Distribution": () => purchaseFlowStore.saveInvestmentProgress(),
};

const cardOnHold = ref(false);

onMounted(async () => {
  const inv = purchaseFlowStore.current_investment;
  const piId = String(inv.funding_account ?? "").startsWith("pi_") ? inv.funding_account : null;
  if (inv.payment_method === "Card" && piId) {
    cardOnHold.value = await stripeCheckout.checkHoldStatus(piId);
  }
});

const snapshot = ref(null);
const isReviewEditModalOpen = ref(false);
const selectedComponentDetails = ref(null);

const isRevisionModalOpen = ref(false);
const suitabilityStructuredIssues = ref([]);
// 'none' | 'has_issues' | 'fixed'
const suitabilityIssueState = ref("none");

const animationData = computed(() => (UIStore.is_dark_mode ? preparingDocDark : preparingDocLight));
const isShowNormalLoader = computed(() => {
  return (
    isRetirementAccountType(purchaseFlowStore.current_investment.type) ||
    (dataStore.s1_offerings_in_quiet_period && purchaseFlowStore.selected_offering?.type === "S1")
  );
});

const stepCardsToShow = computed(() => {
  const stepNamesToIgnore = [
    "Payment Frequency",
    "Amount",
    "Accreditation",
    "Finalize Investment",
    "Term and Yield",
    "Investment Type",
    "IRA Details",
    "Review and Sign",
  ];
  const steps = purchaseFlowStore.investment_steps
    .filter((step) => !stepNamesToIgnore.includes(step.name))
    .map((step) => ({
      ...step,
      label: step.name,
      component: componentMap[step.name] || null,
      save_utility: saveMap[step.name] || (() => Promise.resolve()),
    }));

  return [...steps];
});

function openEditModal(step) {
  const dataStore = useDataStore();

  snapshot.value = {
    contact: cloneDeep(dataStore.contact),
    joint_contacts: cloneDeep(dataStore.joint_contacts),
    entity_accounts: cloneDeep(dataStore.entity_accounts),
    bank_accounts: cloneDeep(dataStore.bank_accounts),
    current_investment: cloneDeep(purchaseFlowStore.current_investment),
    active_joint_contact: cloneDeep(purchaseFlowStore.active_joint_contact),
    active_entity_account: cloneDeep(purchaseFlowStore.active_entity_account),
    mailing_same_as_residential: purchaseFlowStore.mailing_same_as_residential,
    joint_residential_same_as_primary: purchaseFlowStore.joint_residential_same_as_primary,
  };

  selectedComponentDetails.value = step;
  isReviewEditModalOpen.value = true;
}

function handleCancel() {
  if (snapshot.value) {
    // If a new card PI was created during editing, cancel it before discarding.
    const currentPiId = purchaseFlowStore.current_investment.funding_account;
    const snapshotPiId = snapshot.value.current_investment.funding_account;
    if (currentPiId?.startsWith("pi_") && currentPiId !== snapshotPiId) {
      stripeCheckout.cancelPaymentIntent(currentPiId);
    }

    // Restore everything the user may have touched
    Object.assign(dataStore.contact, snapshot.value.contact);
    dataStore.joint_contacts = snapshot.value.joint_contacts;
    dataStore.entity_accounts = snapshot.value.entity_accounts;
    dataStore.bank_accounts = snapshot.value.bank_accounts;
    Object.assign(purchaseFlowStore.current_investment, snapshot.value.current_investment);
    purchaseFlowStore.active_joint_contact = snapshot.value.active_joint_contact;
    purchaseFlowStore.active_entity_account = snapshot.value.active_entity_account;
    purchaseFlowStore.mailing_same_as_residential = snapshot.value.mailing_same_as_residential;
    purchaseFlowStore.joint_residential_same_as_primary =
      snapshot.value.joint_residential_same_as_primary;
    snapshot.value = null;
  }
  isReviewEditModalOpen.value = false;
}

async function handleSaveChanges(componentDetails) {
  await componentDetails.save_utility();
  if (componentDetails.name === "Suitability Questionnaire") {
    syncSuitabilityState();
  }
  snapshot.value = null;
  isReviewEditModalOpen.value = false;
}

function syncSuitabilityState() {
  const remaining = buildSuitabilityIssues();
  const selfErrors = validateSuitabilityForReview();

  const financialIssue = remaining.find((i) => i.type === "financial");
  purchaseFlowStore.suitability_review_error = financialIssue
    ? { messages: financialIssue.details }
    : selfErrors.length
      ? { messages: selfErrors }
      : null;

  if (suitabilityIssueState.value === "none") return;

  if (remaining.length === 0 && selfErrors.length === 0) {
    suitabilityIssueState.value = "fixed";
    purchaseFlowStore.suitability_issue_state = "fixed";
    suitabilityStructuredIssues.value = [];
  } else {
    suitabilityIssueState.value = "has_issues";
    purchaseFlowStore.suitability_issue_state = "has_issues";
    suitabilityStructuredIssues.value = remaining;
  }
}

function openRevisionModal() {
  suitabilityStructuredIssues.value = buildSuitabilityIssues();
  isRevisionModalOpen.value = true;
}

function handleRevisionModalClose() {
  isRevisionModalOpen.value = false;
}

async function handleRevisionModalSave() {
  await purchaseFlowStore.saveOwnerProgress();
  syncSuitabilityState();
  isRevisionModalOpen.value = false;
}

function buildSuitabilityIssues() {
  const suitabilityInfo = dataStore.contact.suitability_info || {};
  const offering = purchaseFlowStore.selected_offering;
  const offeringTermYears = (offering?.bond_term || 0) / 12;

  const EXCLUDED = ["self_responsibility"];
  const questionnaireFields =
    purchaseFlowStore.suitability_questions.find((g) => g.group_name === "Questionnaire")?.fields ??
    [];
  const visibleFields = questionnaireFields.filter((f) => !EXCLUDED.includes(f.field_name));

  function getFieldMeta(fieldName) {
    const idx = visibleFields.findIndex((f) => f.field_name === fieldName);
    const field = visibleFields[idx];
    if (!field) return null;
    return { label: field.label, number: idx + 1, options: field.dropdown_options || [] };
  }

  const issues = [];

  // --- Financial issues (shown as a form in the modal) ---
  const income = Number(String(suitabilityInfo.current_income || "").replace(/,/g, "")) || 0;
  const netWorth = Number(String(suitabilityInfo.current_net_worth || "").replace(/,/g, "")) || 0;

  const isShortTerm = purchaseFlowStore.current_investment?.is_short_term === true;
  const rawAmt = purchaseFlowStore.current_investment?.amount;
  const investmentAmount =
    typeof rawAmt === "string" ? Number(rawAmt.replace(/,/g, "")) : Number(rawAmt) || 0;
  const isShortTermUnderThreshold = isShortTerm && investmentAmount < 10000;
  const s1FinancialCheckApplies = offering?.type === "S1" && !isShortTermUnderThreshold;
  const s1Thresholds = isShortTerm
    ? { income: 45000, netWorth: 45000, netWorthSolo: 150000 }
    : { income: 70000, netWorth: 70000, netWorthSolo: 250000 };
  const fmt = (n) => `$${n.toLocaleString()}`;

  const financialErrors = [];
  if (s1FinancialCheckApplies) {
    const { income: minIncome, netWorth: minNetWorth, netWorthSolo } = s1Thresholds;
    if (!((income >= minIncome && netWorth >= minNetWorth) || netWorth >= netWorthSolo)) {
      financialErrors.push(
        `For this${isShortTerm ? " flex term" : ""} Registered Offering (S-1)${isShortTerm ? " with an investment of $10,000 or more" : ""}, your current annual income and total net worth must each be at least ${fmt(minIncome)}, or your total net worth must be at least ${fmt(netWorthSolo)}.`
      );
    }
  }
  if (financialErrors.length > 0) {
    issues.push({
      type: "financial",
      question_label: "Financial information",
      question_number: null,
      attention_header: "Financial information needs updating",
      details: financialErrors,
      additional_note:
        offering?.type === "S1"
          ? "We will not require supporting documentation to verify the financial information you provide in this section."
          : null,
    });
  }

  if (suitabilityInfo.significant_liquid_net_worth === "No") {
    const meta = getFieldMeta("significant_liquid_net_worth");
    if (meta) {
      issues.push({
        field_name: "significant_liquid_net_worth",
        question_label: meta.label,
        question_number: meta.number,
        attention_header: "Answer update required",
        detail: 'You must select "Yes" to proceed with this investment.',
        unacceptable_answers: meta.options.filter((o) => o === "No"),
        acceptable_answers: meta.options.filter((o) => o === "Yes"),
        current_answer: suitabilityInfo.significant_liquid_net_worth,
      });
    }
  }

  if (suitabilityInfo.risky_investment_understanding === "No") {
    const meta = getFieldMeta("risky_investment_understanding");
    if (meta) {
      issues.push({
        field_name: "risky_investment_understanding",
        question_label: meta.label,
        question_number: meta.number,
        attention_header: "Answer update required",
        detail: 'You must select "Yes" to proceed with this investment.',
        unacceptable_answers: meta.options.filter((o) => o === "No"),
        acceptable_answers: meta.options.filter((o) => o === "Yes"),
        current_answer: suitabilityInfo.risky_investment_understanding,
      });
    }
  }

  const timeHorizon = suitabilityInfo.investment_time_horizon;
  const isTimeHorizonBad =
    (timeHorizon === "1-5 years" && offeringTermYears > 5) ||
    (timeHorizon === "6-10 years" && offeringTermYears > 10);

  if (isTimeHorizonBad) {
    const meta = getFieldMeta("investment_time_horizon");
    if (meta) {
      // Mirror the exact strings checked in validation — anything not on this list is acceptable.
      const explicitlyUnacceptable = [];
      if (offeringTermYears > 5) explicitlyUnacceptable.push("1-5 years");
      if (offeringTermYears > 10) explicitlyUnacceptable.push("6-10 years");

      issues.push({
        field_name: "investment_time_horizon",
        question_label: meta.label,
        question_number: meta.number,
        attention_header: "Investment time horizon too short",
        detail: `Your investment time horizon does not cover this offering's ${offeringTermYears}-year term. Please select a longer time horizon.`,
        unacceptable_answers: meta.options.filter((o) => explicitlyUnacceptable.includes(o)),
        acceptable_answers: meta.options.filter((o) => !explicitlyUnacceptable.includes(o)),
        current_answer: timeHorizon,
      });
    }
  }

  const isEntity = purchaseFlowStore.current_investment?.type === "Entity";
  if (!isEntity && suitabilityInfo.marginal_tax_rate === "Not applicable") {
    const meta = getFieldMeta("marginal_tax_rate");
    if (meta) {
      issues.push({
        field_name: "marginal_tax_rate",
        question_label: meta.label,
        question_number: meta.number,
        attention_header: "Answer update required",
        detail:
          '"Not applicable" is only available for Entity investments. Please select your applicable tax bracket.',
        unacceptable_answers: meta.options.filter((o) => o === "Not applicable"),
        acceptable_answers: meta.options.filter((o) => o !== "Not applicable"),
        current_answer: suitabilityInfo.marginal_tax_rate,
      });
    }
  }

  return issues;
}

function validateSuitabilityForReview() {
  const suitabilityInfo = dataStore.contact.suitability_info || {};
  const errors = [];

  // Financial and selection checks are handled via buildSuitabilityIssues().
  // This only catches the self_responsibility edge case (field excluded from the questionnaire UI).
  if (suitabilityInfo.self_responsibility === "No") {
    errors.push(
      'Your response to "I have conducted my own due diligence, I am making this investment on my own accord, and I will not hold anyone other than myself responsible for any losses that may result from this investment choice." must be "Yes" to proceed.'
    );
  }

  return errors;
}

defineExpose({
  async validate() {
    if (!purchaseFlowStore.isSuitabilityInfoComplete()) {
      const suitabilityStep = stepCardsToShow.value.find(
        (s) => s.name === "Suitability Questionnaire"
      );
      if (suitabilityStep) {
        openEditModal(suitabilityStep);
      }
      UIStore.animateNotificationAlert({
        type: "error",
        icon: "Info",
        message: "Please complete the Suitability Questionnaire before signing your investment.",
      });
      return { proceed: false };
    }

    const allIssues = buildSuitabilityIssues();
    const selfErrors = validateSuitabilityForReview();

    if (allIssues.length || selfErrors.length) {
      const wasAlreadyHasIssues = suitabilityIssueState.value === "has_issues";
      suitabilityStructuredIssues.value = allIssues;
      suitabilityIssueState.value = "has_issues";
      purchaseFlowStore.suitability_issue_state = "has_issues";

      const financialIssue = allIssues.find((i) => i.type === "financial");
      purchaseFlowStore.suitability_review_error = financialIssue
        ? { messages: financialIssue.details }
        : selfErrors.length
          ? { messages: selfErrors }
          : null;

      if (wasAlreadyHasIssues && allIssues.length > 0) {
        isRevisionModalOpen.value = true;
      }

      return { proceed: false };
    }

    purchaseFlowStore.suitability_review_error = null;
    return { proceed: true };
  },
});
</script>
